import { useCallback, useState } from 'react';
import type { TempMediaItem } from '@/modules/inventory/components/canvas/types';
import type { InventoryMediaItem } from '@/modules/inventory/types/boat.types';

/**
 * Holds the committed gallery snapshot of a resumed draft boat (a flat image
 * array plus a featuredIndex). Photos are uploaded elsewhere (the combined
 * upload pipeline); this hook only mirrors server rows loaded via
 * `loadExisting` and resets on `clear`.
 */
export function useUnifiedMediaUpload() {
  const [items, setItems] = useState<TempMediaItem[]>([]);
  const [featuredIndex, setFeaturedIndex] = useState<number>(0);

  const clear = useCallback(() => {
    setItems([]);
    setFeaturedIndex(0);
  }, []);

  /** Load existing media from a resumed draft boat into the gallery.
   *  These items use `clientId: existing-<mediaId>`; tempMediaId stays empty
   *  (they are already inventory_images rows). */
  const loadExisting = useCallback(
    (images: InventoryMediaItem[], primaryImage?: InventoryMediaItem | null) => {
      const loaded: TempMediaItem[] = images.map((img) => ({
        clientId: `existing-${img.id}`,
        tempMediaId: '',
        mediaId: img.id,
        url: img.conversions?.card ?? img.conversions?.list ?? img.url,
        fileName: img.file_name,
        size: img.size ?? 0,
        uploading: false,
        progress: 100,
      }));

      setItems(loaded);

      // Set featured index to primary image if found
      if (primaryImage) {
        const idx = loaded.findIndex((i) => i.mediaId === primaryImage.id);
        setFeaturedIndex(idx >= 0 ? idx : 0);
      } else {
        setFeaturedIndex(0);
      }
    },
    [],
  );

  return { items, featuredIndex, loadExisting, clear };
}
